import { supabase } from './supabase';

// Fetch user profile data with timeout safety
export const getUserProfile = async (userId) => {
  if (!userId) {
    console.error('getUserProfile: User ID is required');
    return null;
  }
  
  // Remove any cache-busting parameters
  const cleanUserId = userId.split('?')[0];
  
  
  // Handle anonymous or temporary users
  if (cleanUserId === 'anonymous-user' || cleanUserId === 'temp-user' || cleanUserId.includes('anonymous')) {
    return {
      id: cleanUserId,
      hasactivesubscription: true,
      subscriptionplan: 'Basic',
      name: 'Guest User',
      email: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      avatar_url: null
    };
  }
  
  // Check if ID is a valid UUID
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleanUserId);
  if (!isValidUUID) {
    console.warn(`getUserProfile: Invalid UUID format: ${cleanUserId}, using default profile`);
    return {
      id: cleanUserId,
      hasactivesubscription: true,
      subscriptionplan: 'Basic',
      name: 'Guest User',
      email: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      avatar_url: null
    };
  }
  
  // Create a timeout promise
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('Profile fetch timed out after 3 seconds'));
    }, 3000);
  });
  
  // Function to get the profile with retry logic
  const fetchProfilePromise = async () => {
    const MAX_RETRIES = 3;
    let retries = 0;
    let lastError = null;
    
    while (retries < MAX_RETRIES) {
    try {
      
      // Check if the profiles table exists first
      try {
        const { error: tableError } = await supabase
          .from('profiles')
          .select('count')
          .limit(1);
          
        if (tableError) {
            console.error('getUserProfile: Error checking profiles table:', tableError);
            throw new Error('Profiles table not accessible');
        }
      } catch (tableCheckError) {
          console.error('getUserProfile: Error during table check:', tableCheckError);
          throw tableCheckError;
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', cleanUserId)
          .single();
        
      if (error) {
        if (error.code === 'PGRST116') {
          return {
            id: cleanUserId,
            hasactivesubscription: false,
            name: '',
            email: '',
            created_at: new Date().toISOString()
          };
        }
        throw error;
      }
      
      
      // Ensure the response has all necessary fields
      const hasActiveSubscription = 
        data?.hasactivesubscription === true || 
        data?.hasactivesubscription === 'true' || 
        data?.hasactivesubscription === 'TRUE' ||
        data?.hasactivesubscription === 1 ||
        data?.hasactivesubscription === '1' ||
        String(data?.hasactivesubscription).toLowerCase() === 'true';
      
      const registrationDate = data?.joined_at || data?.joined || data?.created_at || data?.registration_date || new Date().toISOString();
      const subscriptionStartDate = data?.subscriptionstartdate || 
                                    data?.subscription_start_date || 
                                    data?.subscription_date || 
                                    null;
      
      // Construct a complete profile object
      const profileData = {
        id: data?.id || cleanUserId,
          full_name: data?.name || '',
          name: data?.name || '',
        email: data?.email || '',
        avatar_url: data?.avatar_url || null,
        joined_at: registrationDate,
        created_at: registrationDate,
        updated_at: data?.updated_at || new Date().toISOString(),
        hasactivesubscription: hasActiveSubscription,
        subscriptionplan: data?.subscriptionplan || null,
        subscriptionstartdate: subscriptionStartDate,
        stripe_customer_id: data?.stripe_customer_id || null,
        stripe_subscription_id: data?.stripe_subscription_id || null,
        notification_preferences: data?.notification_preferences || {
          content_updates: true,
          discount_alerts: true,
          subscription_updates: true,
          marketing_emails: false,
          frequency: 'weekly'
        },
          ...data
      };
      
      return profileData;
        
    } catch (error) {
        lastError = error;
        retries++;
        if (retries === MAX_RETRIES) break;
        await new Promise(resolve => setTimeout(resolve, 1000 * retries));
    }
    }
    
    throw lastError;
  };
  
  // Race the fetch against the timeout
  try {
    return await Promise.race([fetchProfilePromise(), timeoutPromise]);
  } catch (error) {
    console.error('getUserProfile: Profile fetch failed or timed out:', error.message);
    return {
      id: cleanUserId,
      hasactivesubscription: false,
      name: '',
      email: '',
      created_at: new Date().toISOString()
    };
  }
};

// Update user profile data
export const updateUserProfile = async (userId, profileData) => {
  if (!userId) {
    console.error('User ID is required to update profile');
    return { success: false, error: 'User ID is required' };
  }
  try {
    // Ensure motivations/context are arrays
    const updateData = {
      ...profileData,
      context: Array.isArray(profileData.context) ? profileData.context : (profileData.context ? [profileData.context] : []),
      motivations: Array.isArray(profileData.motivations) ? profileData.motivations : (profileData.motivations ? [profileData.motivations] : []),
      name: profileData.full_name || profileData.name,
      updated_at: new Date().toISOString(),
    };
    if (updateData.full_name) {
      delete updateData.full_name;
    }
    const { data, error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId);
    if (error) {
      throw error;
    }
    return { success: true };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { success: false, error: error.message };
  }
};

// Fetch all resources from the resources table
export const getResources = async (searchTerm = '', category = 'All', filters = {}) => {
  try {
    let query = supabase
      .from('resources')
      .select('*');

    // Only fetch published resources
    query = query.eq('status', 'published');

    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
    }

    if (category && category !== 'All') {
      query = query.eq('resource_type', category);
    }

    // Tag filter (resource_tags is a comma-separated string, not an array)
    if (filters.tags && filters.tags.length > 0) {
      // Build an or() string for ilike on resource_tags for each tag
      const tagOr = filters.tags.map(tag => `resource_tags.ilike.%${tag}%`).join(',');
      query = query.or(tagOr);
    }

    // Add more filters here if needed (e.g., difficulty, timeframe, author)

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching resources:', error);
      console.error('Error details:', error.message, error.code, error.details);
      return [];
    }

    
    return data.map(resource => ({
      id: resource.id,
      title: resource.name,
      description: resource.description || resource.why_use || '',
      image: resource.banner_url || `https://auth.openorg.fyi/storage/v1/object/public/resources/images/default.webp`,
      category: resource.resource_type || 'Uncategorized',
      author: resource.audience || 'All',
      date: new Date(resource.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      link: resource.link,
      difficulty: resource.difficulty,
      timeframe: resource.timeframe,
      iconUrl: resource.icon_url || null,
      slug: resource.slug,
      type: resource.resource_tags || []
    }));
  } catch (err) {
    console.error('Unexpected error in getResources:', err);
    return [];
  }
};

export const getFeaturedResources = async (limit = 3) => {
  
  try {
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false })  // Order by created_at descending
      .limit(limit);

    if (error) {
      console.error('Error fetching featured resources:', error);
      console.error('Error details:', error.message, error.code, error.details);
      return [];
    }

    
    return data.map(resource => ({
      id: resource.id,
      title: resource.name,
      description: resource.description || resource.why_use || '',
      image: resource.banner_url || `https://auth.openorg.fyi/storage/v1/object/public/resources/images/default.webp`,
      category: resource.resource_type || 'Uncategorized',
      author: resource.audience || 'All',
      date: new Date(resource.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      link: resource.link,
      difficulty: resource.difficulty,
      timeframe: resource.timeframe,
      iconUrl: resource.icon_url || null,
      slug: resource.slug,
      type: resource.resource_tags || []
    }));
  } catch (err) {
    console.error('Unexpected error in getFeaturedResources:', err);
    return [];
  }
};

// Fetch discounts from the discounts table
export const getDiscounts = async (searchTerm = "", category = "All", filters = {}) => {
  try {
    
    // Build the select query explicitly
    const selectQuery = `
      id,
      name,
      offer,
      image_url,
      tags,
      description,
      contact_name,
      contact_email,
      website_url,
      created_at,
      updated_at,
      discount_code,
      discount_icon_url,
      headcount,
      locations,
      oo_recommended_text,
      category,
      slug
    `.replace(/\s+/g, ' ').trim();

    let query = supabase
      .from('discounts')
      .select(selectQuery)
      .order('created_at', { ascending: false });

    // Text search across multiple fields
    if (searchTerm) {
      // Search in basic text fields
      query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,offer.ilike.%${searchTerm}%`);
    }

    // Category filter (array)
    if (category && category !== "All") {
      query = query.overlaps('category', [category]);
    }

    // Location filter (array)
    if (filters.locations && filters.locations.length > 0) {
      query = query.overlaps('locations', filters.locations);
    }

    // Company size filter (array)
    if (filters.headcount && filters.headcount.length > 0) {
      query = query.overlaps('headcount', filters.headcount);
    }

    // Tags filter (array)
    if (filters.tags && filters.tags.length > 0) {
      query = query.overlaps('tags', filters.tags);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching discounts:', error);
      return [];
    }

    let results = data || [];

    // If we have a search term, also search in tags client-side and merge results
    if (searchTerm && results.length >= 0) {
      // Get all discounts to search in tags
      const allDiscountsQuery = supabase
        .from('discounts')
        .select(selectQuery)
        .order('created_at', { ascending: false });

      // Apply the same filters except search term
      if (category && category !== "All") {
        allDiscountsQuery.overlaps('category', [category]);
      }
      if (filters.locations && filters.locations.length > 0) {
        allDiscountsQuery.overlaps('locations', filters.locations);
      }
      if (filters.headcount && filters.headcount.length > 0) {
        allDiscountsQuery.overlaps('headcount', filters.headcount);
      }
      if (filters.tags && filters.tags.length > 0) {
        allDiscountsQuery.overlaps('tags', filters.tags);
      }

      const { data: allData } = await allDiscountsQuery;
      
      if (allData) {
        // Find items that match in tags
        const tagMatches = allData.filter(item => {
          if (!item.tags || !Array.isArray(item.tags)) return false;
          return item.tags.some(tag => 
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          );
        });

        // Merge results and remove duplicates
        const existingIds = new Set(results.map(item => item.id));
        const newMatches = tagMatches.filter(item => !existingIds.has(item.id));
        results = [...results, ...newMatches];
      }
    }

    return results;
  } catch (error) {
    console.error('Error fetching discounts:', error);
    return [];
  }
};

// Fetch a single resource by slug
export const getResourceBySlug = async (slug) => {
  if (!slug) return null;
  try {
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error || !data) {
      console.error('Error fetching resource by slug:', error);
      return null;
    }

    return {
      id: data.id,
      title: data.name,
      description: data.description || data.why_use || '',
      image: data.banner_url || `https://auth.openorg.fyi/storage/v1/object/public/resources/images/default.webp`,
      category: data.resource_type || 'Uncategorized',
      type: data.resource_tags || 'Uncategorized',
      author: data.audience || 'All',
      date: new Date(data.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      link: data.link,
      difficulty: data.difficulty,
      timeframe: data.timeframe,
      iconUrl: data.icon_url || null,
      slug: data.slug,
      banner_url: data.banner_url || null
    };
  } catch (err) {
    console.error('Unexpected error in getResourceBySlug:', err);
    return null;
  }
};

export const getDiscountById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('discounts')
      .select(`
        id,
        name,
        offer,
        image_url,
        tags,
        description,
        contact_name,
        contact_email,
        website_url,
        created_at,
        updated_at,
        discount_code,
        discount_icon_url,
        headcount,
        locations,
        oo_recommended_text,
        category,
        slug
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching discount:', error);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error fetching discount:', error);
    return null;
  }
};

// Favorites operations
export const addToFavorites = async (userId, itemType, itemId) => {
  if (!userId || !itemType || !itemId) {
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    // Check if user is authenticated first
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.id !== userId) {
      return { success: false, error: 'User not authenticated or ID mismatch' };
    }

    // First check if it already exists
    const existing = await isFavorite(userId, itemType, itemId);
    if (existing) {
      return { success: true, data: { user_id: userId, item_type: itemType, item_id: itemId } };
    }

    const { data, error } = await supabase
      .from('favorites')
      .insert({
        user_id: userId,
        item_type: itemType,
        item_id: itemId
      })
      .select()
      .single();

    if (error) {
      // Handle specific Supabase errors
      if (error.code === 'PGRST301') {
        return { success: false, error: 'Permission denied: unable to add favorite' };
      }
      if (error.message?.includes('JWT')) {
        return { success: false, error: 'Authentication required to add favorites' };
      }
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in addToFavorites:', error);
    return { success: false, error: error.message };
  }
};

export const removeFromFavorites = async (userId, itemType, itemId) => {
  if (!userId || !itemType || !itemId) {
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    const { error } = await supabase
      .from('favorites')
      .delete()
      .eq('user_id', userId)
      .eq('item_type', itemType)
      .eq('item_id', itemId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserFavorites = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    const { data, error } = await supabase
      .from('favorites')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const isFavorite = async (userId, itemType, itemId) => {
  if (!userId || !itemType || !itemId) {
    return false;
  }

  try {
    // Check if user is authenticated first
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.id !== userId) {
      console.warn('User not authenticated or ID mismatch in isFavorite');
      return false;
    }

    const { data, error } = await supabase
      .from('favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('item_type', itemType)
      .eq('item_id', itemId)
      .limit(1);

    if (error) {
      // Handle specific Supabase errors
      if (error.code === 'PGRST301') {
        // RLS policy violation
        console.warn('RLS policy prevented access to favorites:', error.message);
        return false;
      }
      if (error.message?.includes('JWT')) {
        console.warn('Authentication issue in isFavorite:', error.message);
        return false;
      }
      console.error('Error checking favorite status:', error);
      return false;
    }

    return data && data.length > 0;
  } catch (error) {
    console.error('Error in isFavorite:', error);
    return false;
  }
};

export const getFavoriteResourcesWithDetails = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    // Get favorite resource IDs
    const { data: favorites, error: favError } = await supabase
      .from('favorites')
      .select('item_id, created_at')
      .eq('user_id', userId)
      .eq('item_type', 'resource')
      .order('created_at', { ascending: false });

    if (favError) {
      console.error('Error fetching favorite resources:', favError);
      return { success: false, error: favError.message };
    }

    if (!favorites || favorites.length === 0) {
      return { success: true, data: [] };
    }

    // Get resource details
    const resourceIds = favorites.map(f => f.item_id);
    const { data: resources, error: resourceError } = await supabase
      .from('resources')
      .select('*')
      .in('id', resourceIds)
      .eq('status', 'published');

    if (resourceError) {
      console.error('Error fetching resource details:', resourceError);
      return { success: false, error: resourceError.message };
    }

    // Map resources to the same format as getResources
    const formattedResources = resources.map(resource => ({
      id: resource.id,
      title: resource.name,
      description: resource.description || resource.why_use || '',
      image: resource.banner_url || `https://auth.openorg.fyi/storage/v1/object/public/resources/images/default.webp`,
      category: resource.resource_type || 'Uncategorized',
      author: resource.audience || 'All',
      date: new Date(resource.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      link: resource.link,
      difficulty: resource.difficulty,
      timeframe: resource.timeframe,
      iconUrl: resource.icon_url || null,
      slug: resource.slug,
      type: resource.resource_tags || [],
      favorited_at: favorites.find(f => f.item_id === resource.id)?.created_at
    }));

    return { success: true, data: formattedResources };
  } catch (error) {
    console.error('Error fetching favorite resources with details:', error);
    return { success: false, error: error.message };
  }
};

export const getFavoriteDiscountsWithDetails = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    // Get favorite discount IDs
    const { data: favorites, error: favError } = await supabase
      .from('favorites')
      .select('item_id, created_at')
      .eq('user_id', userId)
      .eq('item_type', 'discount')
      .order('created_at', { ascending: false });

    if (favError) {
      console.error('Error fetching favorite discounts:', favError);
      return { success: false, error: favError.message };
    }

    if (!favorites || favorites.length === 0) {
      return { success: true, data: [] };
    }

    // Get discount details
    const discountIds = favorites.map(f => f.item_id);
    const { data: discounts, error: discountError } = await supabase
      .from('discounts')
      .select(`
        id,
        name,
        offer,
        image_url,
        tags,
        description,
        contact_name,
        contact_email,
        website_url,
        created_at,
        updated_at,
        discount_code,
        discount_icon_url,
        headcount,
        locations,
        oo_recommended_text,
        category
      `)
      .in('id', discountIds);

    if (discountError) {
      console.error('Error fetching discount details:', discountError);
      return { success: false, error: discountError.message };
    }

    // Add favorited_at timestamp to each discount
    const discountsWithFavoriteTime = discounts.map(discount => ({
      ...discount,
      favorited_at: favorites.find(f => f.item_id === discount.id)?.created_at
    }));

    return { success: true, data: discountsWithFavoriteTime };
  } catch (error) {
    console.error('Error fetching favorite discounts with details:', error);
    return { success: false, error: error.message };
  }
}; 